<template>
  <div class="page-container">
    <div class="page-title-container d-flex justify-content-between align-items-center">
      <h1 class="page-title mb-0">Control de mantenimientos</h1>
      <div>
        <b-button variant="none" class="button button-light mr-2" :to="{ name: 'fleet-maintenance-list' }">
          <i class="ri-history-line mr-1"></i> Historial
        </b-button>
        <b-button variant="none" class="button button-primary" :to="{ name: 'fleet-maintenance-create' }">
          <i class="ri-add-line mr-1"></i> Registrar mantenimiento
        </b-button>
      </div>
    </div>

    <!-- Leyenda de estados -->
    <div class="legend mb-3">
      <span class="legend-item"><span class="dot green"></span> Al día (&gt;1000 km por recorrer)</span>
      <span class="legend-item"><span class="dot yellow"></span> Próximo (≤1000 km)</span>
      <span class="legend-item"><span class="dot red"></span> Vencido</span>
      <span class="legend-item"><span class="dot gray"></span> Sin datos (falta odómetro o historial)</span>
    </div>

    <div class="table-container">
      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-3">Vehículo</th>
              <th>Km actual</th>
              <th>Último mant.</th>
              <th>Próximo (km)</th>
              <th>Restantes</th>
              <th>Ritmo</th>
              <th>Fecha estimada</th>
              <th>Estado</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.vehicle_id">
              <td class="text-center pl-3 font-weight-bold">{{ r.vehicle_plate }}</td>
              <td class="text-center">{{ r.current_km != null ? formatKm(r.current_km) : '—' }}</td>
              <td class="text-center">
                <template v-if="r.last_maintenance_date">
                  {{ formatDate(r.last_maintenance_date) }}
                  <small class="d-block text-muted">{{ formatKm(r.last_maintenance_km) }}</small>
                </template>
                <span v-else class="text-muted">Sin registro</span>
              </td>
              <td class="text-center">{{ r.next_maintenance_km != null ? formatKm(r.next_maintenance_km) : '—' }}</td>
              <td class="text-center">
                <span v-if="r.remaining_km != null" :class="remainingClass(r)">
                  {{ formatKm(r.remaining_km) }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">
                <span v-if="r.km_per_day != null">{{ r.km_per_day.toFixed(1) }} km/día</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center">{{ r.estimated_date ? formatDate(r.estimated_date) : '—' }}</td>
              <td class="text-center">
                <span :class="`badge ${statusMeta(r.status).color}`">{{ statusMeta(r.status).label }}</span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem text="Registrar mantenimiento"
                      @click="$router.push({ name: 'fleet-maintenance-create', query: { vehicle_id: r.vehicle_id } })" />
                    <DropdownItem text="Ver historial"
                      @click="$router.push({ name: 'fleet-maintenance-list', query: { vehicle_id: r.vehicle_id } })" />
                    <DropdownItem text="Configurar intervalo"
                      @click="$router.push({ name: 'fleet-maintenance-config', params: { vehicleId: r.vehicle_id } })" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
            <tr v-if="!rows.length">
              <td colspan="9" class="text-center text-muted py-4">No hay vehículos activos en esta ciudad.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Alert, DropdownTable, DropdownItem } from 'wize-admin'
import { FleetMaintenanceService } from '@/core/services'

const STATUS = {
  green: { label: 'Al día', color: 'badge-green' },
  yellow: { label: 'Próximo', color: 'badge-yellow' },
  red: { label: 'Vencido', color: 'badge-danger' },
  no_data: { label: 'Sin datos', color: 'badge-secondary' }
}

export default {
  name: 'MaintenanceStatus',
  components: { DropdownTable, DropdownItem },
  data() {
    return { rows: [] }
  },
  methods: {
    statusMeta(s) { return STATUS[s] || { label: s, color: 'badge-secondary' } },
    remainingClass(r) {
      if (r.status === 'red') return 'text-danger font-weight-bold'
      if (r.status === 'yellow') return 'text-warning font-weight-bold'
      return ''
    },
    formatKm(v) {
      if (v == null) return '—'
      return `${Number(v).toLocaleString('en-US')} km`
    },
    formatDate(value) {
      if (!value) return '—'
      const parts = String(value).slice(0, 10).split('-')
      if (parts.length !== 3) return value
      return `${parts[2]}/${parts[1]}/${parts[0]}`
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await FleetMaintenanceService.status()
        this.rows = res.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al obtener el estado de mantenimientos')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    }
  },
  async mounted() {
    await this.loadData()
  }
}
</script>

<style lang="stylus" scoped>
.legend
  display flex
  flex-wrap wrap
  gap 18px
  font-size 12px
  color #6c757d
.legend-item
  display flex
  align-items center
  gap 6px
.dot
  width 10px
  height 10px
  border-radius 50%
  display inline-block
  &.green
    background #25C26E
  &.yellow
    background #f59e0b
  &.red
    background #dc3545
  &.gray
    background #adb5bd
</style>
